import React, { useEffect, useState } from 'react';
import { bankTransactionService } from '../services/bankTransactionService';
import type { BankTransaction } from '../types/api';

const BankTransactionsPage: React.FC = () => {
  const [items, setItems] = useState<BankTransaction[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<BankTransaction | null>(null);

  const load = async (p: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await bankTransactionService.getBankTransactions({ search }, { page: p, limit });
      if (res.success && res.data) {
        setItems(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setPage(res.data.pagination.page);
      } else {
        setError(res.message || 'Failed to load');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Bank Transactions (Dev)</h1>
        <form onSubmit={onSearch} className="flex items-center space-x-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search bankref/uniqueid"
            className="border rounded px-3 py-2"
          />
          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">Search</button>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bank Ref</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unique ID</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center">Loading...</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-red-600">{error}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center">No records</td></tr>
            ) : (
              items.map((it) => (
                <tr key={it._id} onClick={() => setSelected(it)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2 text-sm text-gray-700">{new Date(it.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{typeof it.profileId === 'string' ? it.profileId : (it.profileId as any)?.accname || (it.profileId as any)?._id}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{it.date}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{it.time}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{it.amount.toFixed(2)}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{it.bankref}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{it.uniqueid}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => load(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
        <button
          onClick={() => load(page + 1)}
          disabled={page >= totalPages || loading}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>

      {selected && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Bank Transaction Details</h2>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="text-sm text-gray-900">{new Date(selected.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Updated</label>
                    <p className="text-sm text-gray-900">{new Date(selected.updatedAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Profile</label>
                    <p className="text-sm text-gray-900">
                      {typeof selected.profileId === 'string'
                        ? selected.profileId
                        : (selected.profileId as any)?.accname || (selected.profileId as any)?._id}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date</label>
                      <p className="text-sm text-gray-900">{selected.date}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Time</label>
                      <p className="text-sm text-gray-900">{selected.time}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Amount</label>
                      <p className="text-sm text-gray-900">{selected.amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Fees</label>
                      <p className="text-sm text-gray-900">{selected.fees.toFixed(2)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Total</label>
                    <p className="text-sm text-gray-900 font-semibold">{selected.total.toFixed(2)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bank Ref</label>
                    <p className="text-sm text-gray-900">{selected.bankref}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Unique ID</label>
                    <p className="text-sm text-gray-900 font-mono">{selected.uniqueid}</p>
                  </div>
                </div>
              </div>

              {selected.data && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">Raw Data</h3>
                  <pre className="text-xs bg-gray-50 rounded-lg p-4 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(selected.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default BankTransactionsPage;

